import type { EventPublicDTO, MediaListResponse, UploadResponse } from "@wedding/shared-types";
import { apiClient } from "./client.js";
import { getUploaderToken } from "../lib/uploaderToken.js";
import { getUploaderName } from "../lib/uploaderName.js";

export function getEventPublic(token: string): Promise<EventPublicDTO> {
  return apiClient.get(`/api/events/${encodeURIComponent(token)}`);
}

export function verifyEventPassword(
  token: string,
  password: string,
): Promise<{ ok: boolean; name?: string }> {
  return apiClient.post(`/api/events/${encodeURIComponent(token)}/verify`, { password });
}

export function getEventMedia(
  token: string,
  params: { cursor?: string | null; limit?: number } = {},
): Promise<MediaListResponse> {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiClient.get(`/api/events/${encodeURIComponent(token)}/media${suffix}`, {
    headers: { "X-Uploader-Token": getUploaderToken() },
  });
}

/**
 * URL for a ZIP of one or more media — pass `ids` for a selection, or omit
 * it to download every media of the event. Meant to be used as a plain
 * link (`<a href>` / `window.location`), not fetched with JS: the browser
 * streams the download to disk on its own, same as any other file link.
 */
export function downloadZipUrl(token: string, ids?: string[]): string {
  const base = `/api/events/${encodeURIComponent(token)}/download-zip`;
  if (!ids || ids.length === 0) return base;
  return `${base}?ids=${encodeURIComponent(ids.join(","))}`;
}

export interface SingleUploadResult {
  mediaId: string;
  originalName: string;
}

/**
 * One HTTP request per file rather than batching everything into a single
 * multipart body: fetch() has no upload-progress event, so XHR is used, and
 * per-file requests give per-file progress/success/failure/retry for free
 * instead of having to interpret a shared byte counter across many files.
 */
export function uploadSingleFile(
  token: string,
  file: File,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<SingleUploadResult> {
  const formData = new FormData();
  formData.append("files", file, file.name);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/events/${encodeURIComponent(token)}/upload`);
    xhr.setRequestHeader("X-Uploader-Token", getUploaderToken());
    const uploaderName = getUploaderName();
    // Header values are ASCII-only — percent-encode the (possibly accented)
    // name; the VPS decodes it back before storing it.
    if (uploaderName) xhr.setRequestHeader("X-Uploader-Name", encodeURIComponent(uploaderName));
    xhr.upload.onprogress = (evt) => {
      if (evt.lengthComputable && onProgress) onProgress(evt.loaded / evt.total);
    };
    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText) as UploadResponse & { error?: string };
        if (xhr.status >= 200 && xhr.status < 300) {
          const uploaded = body.uploaded?.[0];
          const failed = body.failed?.[0];
          if (uploaded?.mediaId) {
            resolve({ mediaId: uploaded.mediaId, originalName: uploaded.originalName });
          } else {
            reject(new Error(failed?.error ?? "Échec de l'envoi"));
          }
        } else {
          reject(new Error(body.error ?? `Échec de l'envoi (code ${xhr.status})`));
        }
      } catch {
        reject(new Error(`Réponse invalide du serveur (code ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error("Erreur réseau pendant l'envoi"));
    xhr.onabort = () => reject(new DOMException("Envoi annulé", "AbortError"));
    signal?.addEventListener("abort", () => xhr.abort());
    xhr.send(formData);
  });
}
